import { NewV3 } from "@circle-react/components/Spaces/NewV3";
import type { SpaceType } from "@circle-react/types/Space";
import { Modal } from "@circleco/compass/components/Modal";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import type { SpacePrefillData } from "@/react/components/SettingsApp/Spaces/helpers/spacePrefillHelpers";

import { STEP } from "./constants";
import { useCreateSpaceState } from "./hooks/useCreateSpaceState";
import { CourseTypeStep } from "./steps/CourseTypeStep";
import { SpaceTypeStep } from "./steps/SpaceTypeStep";
import { primaryActionForStep, titleForStep } from "./utils";

export interface CreateSpaceModalProps {
  currentSpaceGroupId?: number;
  initialPostType?: SpaceType;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  prefillData?: SpacePrefillData;
}

export const CreateSpaceModal = ({
  open,
  onOpenChange,
  currentSpaceGroupId,
  initialPostType,
  prefillData,
}: CreateSpaceModalProps) => {
  const { step, selectType, confirmCourse, back, reset } =
    useCreateSpaceState(initialPostType);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const wasOpenRef = useRef(open);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!wasOpenRef.current && open) {
      reset();
    }
    wasOpenRef.current = open;
  }, [open, reset]);

  const handleClose = () => onOpenChange(false);
  const primaryAction = primaryActionForStep(
    step,
    isSubmitting,
    isFormDisabled,
  );
  const shouldShowBackButton = !(
    initialPostType === "course" && step.id === STEP.course
  );

  return (
    <Modal.Root
      size={isMobile ? "full-screen" : "lg"}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Modal.Content>
        <Modal.Header
          title={titleForStep(step)}
          showBackButton={step.id !== STEP.type && shouldShowBackButton}
          onBackButtonClick={back}
        />
        <Modal.Body>
          {step.id === STEP.type && (
            <SpaceTypeStep
              initialValue={initialPostType ?? "basic"}
              onSelect={selectType}
            />
          )}
          {step.id === STEP.course && (
            <CourseTypeStep onConfirm={confirmCourse} />
          )}
          {step.id === STEP.form && (
            <NewV3
              onClose={handleClose}
              post_type={step.postType}
              courseSetting={step.courseSetting}
              currentSpaceGroupId={currentSpaceGroupId}
              prefillData={prefillData}
              setExternalDisabledState={setIsFormDisabled}
              setExternalLoadingState={setIsSubmitting}
            />
          )}
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
            onClick: handleClose,
            shouldCloseModal: false,
          }}
          primaryAction={{
            label: primaryAction.label,
            type: "submit",
            form: primaryAction.form,
            loading: primaryAction.loading,
            disabled: primaryAction.disabled,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
